import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BlogPost } from '@/types/blog';
import { formatDate, calculateReadingTime } from '@/utils/blogUtils';
import { Link } from 'react-router-dom';

interface BlogCardProps {
  post: BlogPost;
}

const BlogCard = ({ post }: BlogCardProps) => {
  // Use pre-calculated reading time if available, otherwise calculate it
  const readingTime = post.readingTime || calculateReadingTime(post.content);

  return (
    <Card className="h-full flex flex-col hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-gray-600 bg-gray-800/50 backdrop-blur-sm hover:bg-gray-800/70 hover:border-coderide-light-blue/50">
      <CardHeader className="pb-4">
        {post.featured && (
          <Badge variant="secondary" className="w-fit mb-2 bg-coderide-light-blue/10 text-coderide-light-blue border-coderide-light-blue/20">
            Featured
          </Badge>
        )}
        
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {post.tags.slice(0, 3).map((tag, index) => (
              <Badge 
                key={index} 
                variant="outline" 
                className="text-xs bg-coderide-purple/10 text-coderide-purple border-coderide-purple/30 hover:bg-coderide-purple/20"
              >
                {tag}
              </Badge>
            ))}
            {post.tags.length > 3 && (
              <Badge 
                variant="outline" 
                className="text-xs bg-gray-700/50 text-gray-400 border-gray-600"
              >
                +{post.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        <CardTitle className="text-xl font-semibold leading-tight text-foreground hover:text-coderide-light-blue transition-colors">
          <Link to={`/blog/${post.slug}`} className="block">
            {post.title}
          </Link>
        </CardTitle>
        <CardDescription className="text-muted-foreground line-clamp-2">
          {post.excerpt}
        </CardDescription>
      </CardHeader>
      
      {post.image && (
        <CardContent className="pb-4">
          <Link to={`/blog/${post.slug}`}>
            <img 
              src={post.image} 
              alt={post.imageAlt || post.title}
              width={1200}
              height={630}
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              decoding="async"
              loading="lazy"
              className="w-full h-48 object-cover rounded-lg hover:opacity-90 transition-opacity"
            />
          </Link>
        </CardContent>
      )}
      
      <CardFooter className="mt-auto pt-4 flex flex-col gap-3">
        <div className="flex justify-between items-center w-full text-sm text-muted-foreground">
          <span>{formatDate(post.date)}</span>
          <span>{readingTime}</span>
        </div>
        
        <div className="flex justify-between items-center w-full">
          <span className="text-sm text-muted-foreground">
            By {post.author}
          </span>
          <Button 
            variant="outline" 
            size="sm" 
            asChild
            className="hover:bg-coderide-purple hover:text-white hover:border-coderide-purple transition-colors"
          >
            <Link to={`/blog/${post.slug}`}>
              Read More
            </Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export default BlogCard;
